/* eslint-disable react/forbid-prop-types */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import { GhostButton, PrimaryButton } from '../../../../styles/ui/Button';

import Modal from '../../../../components/Modal/Modal';
import HFIcon from '../icons';
import useGoogleMaps from '../../../../hooks/useGoogleMaps';
import {
   updateItemLocation,
   firstErrorCode,
} from '../../../../services/routing/routingScheduleApi';
import { updateScheduleItem } from '../../../../services/routing/routingApi';

// =============================================================================
// LocationPickerModal — ajuste manual de geolocalização do item (handoff 0043).
//
// Conceito (Caio, 2026-06-25): o modal se explica sozinho por uma MÁQUINA DE
// ESTADOS do ponto + duas ZONAS, em duas colunas na zona de definição.
//
//   pointStatus:  locating → suggested ─┐
//                       └→ notfound  ───┴→ defined → (salva)
//   Regra: MEXER = CONFIRMAR (buscar/clicar/arrastar → defined). Ponto SUGERIDO
//   (auto-geocode) nasce provisório (pino âmbar); ao mexer vira definido (pino
//   vermelho).
//
//   Corpo "Defina a localização" (2 colunas):
//     • esquerda: endereço do pedido (referência) — campos rotulados read-only.
//     • direita:  busca + mapa (as ferramentas).
//   Rodapé (ao lado das ações Cancelar/Salvar): RESUMO do ponto — item VINCULADO
//     → read-only "O pino está sobre" (endereço estruturado, não se reescreve
//     aqui); item SEM vínculo → "Novo endereço" editável (gerado do ponto, vai
//     pro service_address legado). No fallback SEM mapa, lat/long entram no resumo.
//
//   lat/long NÃO são exibidos no modo mapa (parâmetro da engine, não info de
//   roteirização visual — o pino é a fonte). Só aparecem no fallback SEM mapa,
//   onde são a única via de entrada.
//
// Referência = 6 campos atomizados (Logradouro, Nº, Complemento, Município,
//   Estado, CEP) do customer_addresses vinculado (handoff 0044 / ADR-009),
//   read-only. Item SEM vínculo (customerAddressId == null: manual / import sem
//   código de cliente) cai no fallback de 2 campos mesclados (service_address +
//   service_address_complement).
//
// Persistência ao salvar: PATCH .../location (lat/long, destrava). Item SEM
// vínculo também grava o texto editado em service_address (PUT /routing/items/
// :id). Item VINCULADO NÃO escreve texto — o endereço estruturado tem dono no
// cadastro do cliente (ADR-009); este modal só fixa o ponto.
// =============================================================================

const FALLBACK_CENTER = { lat: -16.6869, lng: -49.2648 };

const Body = styled.div`
   display: flex;
   flex-direction: column;
   gap: 14px;
   font-size: 0.85rem;
   color: ${({ theme }) => theme?.colors?.textPrimary ?? '#222'};

   /* Trava a altura ao viewport e rola por dentro (header/footer do modal ficam
      fixos) — corrige o corte de topo/rodapé em telas menores. O Modal
      compartilhado não impõe max-height; a trava fica local a este modal. O
      rodapé carrega o resumo do ponto (linha inline compacta + botões). */
   max-height: calc(100vh - 240px);
   overflow-y: auto;
   overflow-x: hidden;
`;

const Zone = styled.section`
   border: 1px solid rgba(0, 0, 0, 0.1);
   border-radius: 10px;
   padding: 12px;
   display: flex;
   flex-direction: column;
   gap: 10px;
`;

const ZoneTitle = styled.div`
   font-size: 0.7rem;
   font-weight: 800;
   text-transform: uppercase;
   letter-spacing: 0.05em;
   color: ${({ theme }) => theme?.colors?.textSecondary ?? '#888'};
`;

// Duas colunas: referência (esquerda) | busca+mapa (direita). Empilha no estreito.
// A confirmação do ponto saiu p/ o rodapé (resumo ao lado dos botões de ação).
const DefineGrid = styled.div`
   display: grid;
   grid-template-columns: minmax(0, 0.78fr) minmax(0, 1.45fr);
   gap: 14px;
   align-items: start;

   @media (max-width: 640px) {
      grid-template-columns: 1fr;
   }

   .ref,
   .tools {
      display: flex;
      flex-direction: column;
      gap: 8px;
   }
`;

const RefCaption = styled.div`
   font-size: 0.68rem;
   font-weight: 700;
   text-transform: uppercase;
   letter-spacing: 0.04em;
   color: ${({ theme }) => theme?.colors?.textSecondary ?? '#777'};
`;

// Endereço atomizado (handoff 0044): campos rotulados read-only. Inputs sem
// fonte mono (ao contrário do lat/lng) — é texto de endereço, não coordenada.
// width/min-width 0 ⇒ o input encolhe à célula do grid (não estoura sobre o mapa).
const AddrField = styled.div`
   display: flex;
   flex-direction: column;
   gap: 4px;
   min-width: 0;

   label {
      font-size: 0.72rem;
      font-weight: 600;
      color: ${({ theme }) => theme?.colors?.textSecondary ?? '#555'};
   }

   input {
      width: 100%;
      min-width: 0;
      padding: 7px 9px;
      font-size: 0.82rem;
      border-radius: 8px;
      border: 1px solid
         ${({ theme }) => theme?.colors?.border ?? 'rgba(0,0,0,0.15)'};
      font-family: inherit;
      background: rgba(0, 0, 0, 0.04);
      color: ${({ theme }) => theme?.colors?.textSecondary ?? '#555'};
      cursor: default;
   }
`;

// Campos longos de texto livre (Logradouro, Complemento) como caixa read-only
// que QUEBRA linha e cresce com o conteúdo — não trunca como um <input>.
const ReadBox = styled.div`
   padding: 7px 9px;
   font-size: 0.82rem;
   line-height: 1.4;
   border-radius: 8px;
   border: 1px solid ${({ theme }) => theme?.colors?.border ?? 'rgba(0,0,0,0.15)'};
   background: rgba(0, 0, 0, 0.04);
   color: ${({ theme }) => theme?.colors?.textSecondary ?? '#555'};
   white-space: pre-wrap;
   word-break: break-word;
   min-height: 34px;
`;

// Número curto + CEP: ambos enxutos (a coluna esquerda é estreita p/ dar mais
// largura ao mapa).
const RefRowNumCep = styled.div`
   display: grid;
   grid-template-columns: minmax(0, 0.85fr) minmax(0, 1fr);
   gap: 8px;
`;

// Município ocupa o resto; Estado (UF, 2 letras) fixo bem estreito — sem sobra.
const RefRowCityState = styled.div`
   display: grid;
   grid-template-columns: minmax(0, 1fr) 64px;
   gap: 8px;
`;

const TONES = {
   wait: { bg: 'rgba(0,0,0,0.04)', fg: '#555', bd: 'rgba(0,0,0,0.12)' },
   warn: { bg: 'rgba(245,158,11,0.1)', fg: '#92400e', bd: 'rgba(245,158,11,0.45)' },
   ok: { bg: 'rgba(16,185,129,0.1)', fg: '#065f46', bd: 'rgba(16,185,129,0.45)' },
   info: { bg: 'rgba(0,0,0,0.04)', fg: '#555', bd: 'rgba(0,0,0,0.12)' },
};

const Narrator = styled.div`
   display: flex;
   align-items: flex-start;
   gap: 8px;
   font-size: 0.8rem;
   line-height: 1.45;
   padding: 9px 11px;
   border-radius: 8px;
   background: ${({ $tone }) => TONES[$tone].bg};
   color: ${({ $tone }) => TONES[$tone].fg};
   border: 1px solid ${({ $tone }) => TONES[$tone].bd};

   svg {
      flex-shrink: 0;
      margin-top: 1px;
   }
`;

const MapBox = styled.div`
   width: 100%;
   height: 300px;
   border-radius: 10px;
   overflow: hidden;
   border: 1px solid rgba(0, 0, 0, 0.12);

   > div {
      width: 100%;
      height: 100%;
   }
`;

const Note = styled.div`
   display: flex;
   align-items: center;
   gap: 5px;
   font-size: 0.72rem;
   line-height: 1.4;
   color: ${({ $error }) => ($error ? '#b71c1c' : 'rgba(0,0,0,0.5)')};

   svg {
      width: 12px;
      height: 12px;
      flex-shrink: 0;
   }
`;

const Field = styled.div`
   display: flex;
   flex-direction: column;
   gap: 4px;

   label {
      font-size: 0.75rem;
      font-weight: 600;
      color: ${({ theme }) => theme?.colors?.textSecondary ?? '#555'};
   }

   textarea,
   input {
      padding: 8px 10px;
      font-size: 0.85rem;
      border-radius: 8px;
      border: 1px solid
         ${({ theme }) => theme?.colors?.border ?? 'rgba(0,0,0,0.15)'};
      font-family: inherit;
   }

   textarea {
      resize: vertical;
      min-height: 44px;
      line-height: 1.4;
   }

   textarea[readonly],
   input[readonly] {
      background: rgba(0, 0, 0, 0.04);
      color: ${({ theme }) => theme?.colors?.textSecondary ?? '#555'};
      cursor: default;
      resize: none;
   }

   input[readonly] {
      font-family: var(--hf-mono, monospace);
   }
`;

const Grid = styled.div`
   display: grid;
   grid-template-columns: 1fr 1fr;
   gap: 12px;
`;

const SearchRow = styled.form`
   display: flex;
   gap: 6px;

   input {
      flex: 1;
      min-width: 0;
      padding: 8px 10px;
      font-size: 0.85rem;
      border-radius: 8px;
      border: 1px solid
         ${({ theme }) => theme?.colors?.border ?? 'rgba(0,0,0,0.15)'};
      font-family: inherit;
   }

   button {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 0 12px;
      border-radius: 8px;
      border: 0;
      background: #1f1f18;
      color: #fff;
      cursor: pointer;
   }

   button:disabled {
      opacity: 0.5;
      cursor: not-allowed;
   }
`;

const ErrorBox = styled.div`
   background: rgba(211, 47, 47, 0.08);
   border: 1px solid rgba(211, 47, 47, 0.4);
   border-radius: 8px;
   padding: 9px 12px;
   font-size: 0.8rem;
   color: #b71c1c;
   line-height: 1.45;
`;

// ── Rodapé: resumo do ponto (esquerda) + ações (direita) ────────────────────
// A confirmação do ponto virou um RESUMO no rodapé, ao lado dos botões — fica
// junto do gesto "salvar" e libera o corpo p/ 2 colunas (referência | mapa).
const FooterBar = styled.div`
   width: 100%;
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 18px;

   @media (max-width: 640px) {
      flex-direction: column;
      align-items: stretch;
   }
`;

const FooterSummary = styled.div`
   flex: 1;
   min-width: 0;
   display: flex;
   flex-direction: column;
   gap: 4px;
   text-align: left;
`;

// Linha inline "O pino está sobre <endereço>" — texto normal + endereço em
// negrito. Compacta a altura do rodapé (antes: caption + label + caixa).
const SummaryLine = styled.div`
   font-size: 0.85rem;
   line-height: 1.4;
   color: ${({ theme }) => theme?.colors?.textSecondary ?? '#555'};

   strong {
      font-weight: 700;
      color: ${({ theme }) => theme?.colors?.textPrimary ?? '#222'};
   }
`;

const FooterActions = styled.div`
   display: flex;
   gap: 10px;
   flex-shrink: 0;

   @media (max-width: 640px) {
      justify-content: flex-end;
   }
`;

// Pino: âmbar = SUGERIDO (provisório); vermelho = DEFINIDO (confirmado).
function pinIconUrl(suggested) {
   const fill = suggested ? '#F59E0B' : '#D32F2F';
   const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="36" height="48" viewBox="0 0 36 48"><path d="M18 1C9.7 1 3 7.7 3 16c0 10.5 13.2 28.6 13.8 29.4a1.5 1.5 0 0 0 2.4 0C19.8 44.6 33 26.5 33 16 33 7.7 26.3 1 18 1z" fill="${fill}" stroke="#FFFFFF" stroke-width="2"/><circle cx="18" cy="16" r="5.5" fill="#FFFFFF"/></svg>`;
   return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

function pinIcon(g, suggested) {
   return {
      url: pinIconUrl(suggested),
      scaledSize: new g.maps.Size(36, 48),
      anchor: new g.maps.Point(18, 46),
   };
}

// Bias regional pela base operacional p/ o forward geocode (endereços esparsos
// do Refiltek). `bounds` é só PREFERÊNCIA — não exclui pontos legítimos fora.
function biasBoundsFor(g, base) {
   if (!base || base.latitude == null || base.longitude == null) return null;
   const lat = Number(base.latitude);
   const lng = Number(base.longitude);
   if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
   return new g.maps.LatLngBounds(
      { lat: lat - 0.4, lng: lng - 0.4 },
      { lat: lat + 0.4, lng: lng + 0.4 }
   );
}

function initialCoord(stop, base) {
   if (stop?.lat != null && stop?.lng != null) {
      return { lat: stop.lat, lng: stop.lng, fromItem: true };
   }
   if (base?.latitude != null && base?.longitude != null) {
      return {
         lat: Number(base.latitude),
         lng: Number(base.longitude),
         fromItem: false,
      };
   }
   return { ...FALLBACK_CENTER, fromItem: false };
}

// Referência atomizada (handoff 0044): célula vazia vira "—"; CEP cru de 8
// dígitos ganha máscara só na exibição.
function orDash(value) {
   return value != null && String(value).trim() !== '' ? String(value) : '—';
}
function formatCep(zip) {
   if (zip == null || String(zip).trim() === '') return '—';
   const digits = String(zip).replace(/\D/g, '');
   if (digits.length === 8) return `${digits.slice(0, 5)}-${digits.slice(5)}`;
   return String(zip);
}

function isValidLat(n) {
   return Number.isFinite(n) && n >= -90 && n <= 90;
}
function isValidLng(n) {
   return Number.isFinite(n) && n >= -180 && n <= 180;
}

function narratorFor({ noMap, mapLoading, pointStatus }) {
   if (noMap) {
      return {
         tone: 'info',
         icon: HFIcon.pin,
         text: 'Mapa indisponível — informe a latitude e a longitude manualmente.',
      };
   }
   if (mapLoading) {
      return { tone: 'wait', icon: HFIcon.clock, text: 'Carregando mapa…' };
   }
   switch (pointStatus) {
      case 'locating':
         return {
            tone: 'wait',
            icon: HFIcon.clock,
            text: 'Procurando o endereço no mapa…',
         };
      case 'suggested':
         return {
            tone: 'warn',
            icon: HFIcon.alert,
            text: 'Encontrei um ponto aproximado. Confira se corresponde ao endereço do pedido e ajuste se precisar.',
         };
      case 'notfound':
         return {
            tone: 'warn',
            icon: HFIcon.alert,
            text: 'Não localizei automaticamente. Pesquise o endereço ou clique no mapa para marcar o ponto.',
         };
      case 'defined':
      default:
         return {
            tone: 'ok',
            icon: HFIcon.check,
            text: 'Ponto definido. Confira o endereço gerado e salve.',
         };
   }
}

export default function LocationPickerModal({
   itemId,
   stop,
   base = null,
   onClose,
   onSaved,
}) {
   const { apiKey, isLoaded, loadError } = useGoogleMaps();
   const useMap = Boolean(apiKey && isLoaded && !loadError);
   const noMap = !apiKey || Boolean(loadError);
   const mapLoading = Boolean(apiKey) && !isLoaded && !loadError;

   const start = useRef(initialCoord(stop, base)).current;
   const originalAddress = (stop?.raw?.service_address || stop?.addr || '').trim();
   const originalComplement = (stop?.raw?.service_address_complement || '').trim();

   // Endereço atomizado vindo do customer_addresses vinculado (handoff 0044).
   // customerAddressId != null ⇒ item vinculado: exibe os 6 campos rotulados.
   // null ⇒ sem vínculo: cai no fallback de 2 campos mesclados acima.
   const addr = stop?.address ?? {};
   const hasAtomized = stop?.customerAddressId != null;

   const [coord, setCoord] = useState(
      start.fromItem ? { lat: start.lat, lng: start.lng } : null
   );
   const [latInput, setLatInput] = useState(
      start.fromItem ? String(start.lat) : ''
   );
   const [lngInput, setLngInput] = useState(
      start.fromItem ? String(start.lng) : ''
   );
   const [addressDraft, setAddressDraft] = useState(originalAddress);
   const [pointStatus, setPointStatus] = useState(
      start.fromItem ? 'defined' : 'locating'
   );
   const [searchInput, setSearchInput] = useState('');
   const [searchStatus, setSearchStatus] = useState(null); // null|'searching'|'notfound'

   const [isSaving, setIsSaving] = useState(false);
   const [error, setError] = useState(null);

   const containerRef = useRef(null);
   const mapRef = useRef(null);
   const markerRef = useRef(null);
   const geocoderRef = useRef(null);
   const searchInputRef = useRef(null);
   const aliveRef = useRef(true);
   useEffect(() => {
      aliveRef.current = true;
      return () => {
         aliveRef.current = false;
      };
   }, []);

   const reverseGeocode = useCallback((lat, lng) => {
      const geocoder = geocoderRef.current;
      if (!geocoder) return;
      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
         if (!aliveRef.current) return;
         if (status === 'OK' && results && results[0]) {
            setAddressDraft(results[0].formatted_address);
         }
      });
   }, []);

   const applyPoint = useCallback(
      (lat, lng, { address = null, confirmed = true } = {}) => {
         setCoord({ lat, lng });
         setLatInput(String(lat));
         setLngInput(String(lng));
         setError(null);
         setPointStatus(confirmed ? 'defined' : 'suggested');
         if (address != null) setAddressDraft(address);
         else reverseGeocode(lat, lng);
      },
      [reverseGeocode]
   );

   useEffect(() => {
      if (!useMap) return;
      const g = window.google;
      if (!g || !containerRef.current || mapRef.current) return;

      geocoderRef.current = new g.maps.Geocoder();
      mapRef.current = new g.maps.Map(containerRef.current, {
         center: { lat: start.lat, lng: start.lng },
         zoom: start.fromItem ? 16 : 13,
         mapTypeControl: false,
         streetViewControl: false,
         fullscreenControl: false,
         clickableIcons: false,
         keyboardShortcuts: false,
         gestureHandling: 'greedy',
         styles: [
            { featureType: 'poi.business', stylers: [{ visibility: 'off' }] },
         ],
      });

      mapRef.current.addListener('click', (e) => {
         applyPoint(e.latLng.lat(), e.latLng.lng());
      });

      if (start.fromItem) return;

      if (!originalAddress) {
         setPointStatus('notfound');
         return;
      }

      setPointStatus('locating');
      const bias = biasBoundsFor(g, base);
      const request = {
         address: originalAddress,
         componentRestrictions: { country: 'BR' },
      };
      if (bias) request.bounds = bias;
      geocoderRef.current.geocode(request, (results, status) => {
         if (!aliveRef.current) return;
         if (status === 'OK' && results && results[0]) {
            const loc = results[0].geometry.location;
            applyPoint(loc.lat(), loc.lng(), {
               address: results[0].formatted_address,
               confirmed: false,
            });
            mapRef.current.panTo(loc);
            mapRef.current.setZoom(16);
         } else {
            setPointStatus('notfound');
         }
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [useMap]);

   useEffect(() => {
      if (!useMap || !mapRef.current || !coord) return;
      const g = window.google;
      const icon = pinIcon(g, pointStatus === 'suggested');
      if (!markerRef.current) {
         markerRef.current = new g.maps.Marker({
            position: coord,
            map: mapRef.current,
            draggable: true,
            icon,
         });
         markerRef.current.addListener('dragend', (e) => {
            applyPoint(e.latLng.lat(), e.latLng.lng());
         });
      } else {
         markerRef.current.setPosition(coord);
         markerRef.current.setIcon(icon);
      }
   }, [coord, useMap, applyPoint, pointStatus]);

   useEffect(() => {
      if (useMap && pointStatus === 'notfound') {
         searchInputRef.current?.focus();
      }
   }, [useMap, pointStatus]);

   const handleLatInput = (value) => {
      setLatInput(value);
      const lat = Number(value);
      const lng = Number(lngInput);
      if (value.trim() !== '' && isValidLat(lat)) {
         setCoord({ lat, lng: Number.isFinite(lng) ? lng : start.lng });
         setPointStatus('defined');
      }
   };
   const handleLngInput = (value) => {
      setLngInput(value);
      const lng = Number(value);
      const lat = Number(latInput);
      if (value.trim() !== '' && isValidLng(lng)) {
         setCoord({ lat: Number.isFinite(lat) ? lat : start.lat, lng });
         setPointStatus('defined');
      }
   };

   const handleAddressSearch = (e) => {
      e.preventDefault();
      const q = searchInput.trim();
      const geocoder = geocoderRef.current;
      if (!q || !geocoder) return;
      const g = window.google;
      const bias = biasBoundsFor(g, base);
      const request = { address: q, componentRestrictions: { country: 'BR' } };
      if (bias) request.bounds = bias;
      setSearchStatus('searching');
      geocoder.geocode(request, (results, status) => {
         if (!aliveRef.current) return;
         if (status === 'OK' && results && results[0]) {
            const loc = results[0].geometry.location;
            applyPoint(loc.lat(), loc.lng(), {
               address: results[0].formatted_address,
            });
            if (mapRef.current) {
               mapRef.current.panTo(loc);
               mapRef.current.setZoom(16);
            }
            setSearchStatus(null);
         } else {
            setSearchStatus('notfound');
         }
      });
   };

   const handleSave = async () => {
      setError(null);
      const lat = Number(latInput);
      const lng = Number(lngInput);

      if (!coord || latInput.trim() === '' || lngInput.trim() === '') {
         setError('Marque o ponto no mapa (ou informe latitude e longitude).');
         return;
      }
      if (!isValidLat(lat)) {
         setError('Latitude fora da faixa válida (-90 a 90).');
         return;
      }
      if (!isValidLng(lng)) {
         setError('Longitude fora da faixa válida (-180 a 180).');
         return;
      }
      if (lat === 0 && lng === 0) {
         setError('Ponto inválido (0, 0). Posicione o pino sobre o endereço.');
         return;
      }

      setIsSaving(true);
      try {
         const updatedLoc = await updateItemLocation(itemId, {
            latitude: lat,
            longitude: lng,
         });
         let finalItem = updatedLoc;

         // Item vinculado (handoff 0044): o endereço é estruturado e tem dono no
         // cadastro do cliente — este modal só fixa o ponto (lat/lng), não
         // reescreve texto. Item SEM vínculo mantém o escape hatch legado: grava
         // o texto editado em service_address.
         const newAddr = addressDraft.trim();
         if (!hasAtomized && newAddr && newAddr !== originalAddress) {
            try {
               const res = await updateScheduleItem(itemId, {
                  service_address: newAddr,
               });
               finalItem = res.scheduleItem || finalItem;
            } catch {
               toast.warn(
                  'Localização salva, mas não foi possível atualizar o endereço por escrito.'
               );
            }
         }

         toast.success('Geolocalização atualizada com sucesso.');
         onSaved?.({ itemId, scheduleItem: finalItem });
         onClose();
      } catch (err) {
         const code = firstErrorCode(err);
         const apiMessage = err?.response?.data?.message;
         const byCode = {
            invalid_state_for_location:
               'Só itens pendentes aceitam ajuste de localização.',
            item_already_routed:
               'Item já está em uma rota — remova-o da rota para ajustar a localização.',
            format: 'Coordenadas fora da faixa válida. Reposicione o ponto.',
            required: 'Informe latitude e longitude.',
            schedule_item_not_found:
               'Item não encontrado. Recarregue o agendamento.',
         };
         setError(
            byCode[code] ?? apiMessage ?? 'Não foi possível salvar a localização.'
         );
         setIsSaving(false);
      }
   };

   const narrator = narratorFor({ noMap, mapLoading, pointStatus });
   const canSave = Boolean(coord) && !isSaving;
   let saveLabel = 'Salvar localização';
   if (isSaving) saveLabel = 'Salvando…';
   else if (pointStatus === 'suggested') saveLabel = 'Confirmar e salvar';

   // Resumo do ponto — vive no rodapé, ao lado dos botões. Item VINCULADO →
   // read-only "O pino está sobre" (endereço estruturado tem dono no cadastro);
   // SEM vínculo → "Novo endereço" editável (service_address legado). No fallback
   // SEM mapa, os campos lat/long entram aqui (única via de entrada).
   const confirmationBlock = (
      <FooterSummary>
         {hasAtomized ? (
            <>
               <SummaryLine>
                  O pino está sobre{' '}
                  <strong id="geo-address">
                     {addressDraft.trim() || '—'}
                  </strong>
               </SummaryLine>
               <Note>
                  Compare com o endereço do pedido (à esquerda). Para alterar o
                  endereço escrito, edite o cadastro do cliente.
               </Note>
            </>
         ) : (
            <>
               <Field>
                  <label htmlFor="geo-address">Novo endereço</label>
                  <textarea
                     id="geo-address"
                     value={addressDraft}
                     placeholder="Endereço gerado pelo ponto no mapa (editável)"
                     onChange={(e) => setAddressDraft(e.target.value)}
                     disabled={isSaving}
                  />
               </Field>
               <Note>
                  Gerado pelo ponto · editável — corrigir o texto aqui não move o
                  pino.
               </Note>
            </>
         )}

         {noMap && (
            <Grid>
               <Field>
                  <label htmlFor="geo-lat">Latitude</label>
                  <input
                     id="geo-lat"
                     type="text"
                     inputMode="decimal"
                     value={latInput}
                     placeholder="—"
                     onChange={(e) => handleLatInput(e.target.value)}
                     disabled={isSaving}
                  />
               </Field>
               <Field>
                  <label htmlFor="geo-lng">Longitude</label>
                  <input
                     id="geo-lng"
                     type="text"
                     inputMode="decimal"
                     value={lngInput}
                     placeholder="—"
                     onChange={(e) => handleLngInput(e.target.value)}
                     disabled={isSaving}
                  />
               </Field>
            </Grid>
         )}

         {pointStatus === 'suggested' && (
            <Note $error>
               Confira se corresponde ao endereço do pedido antes de confirmar.
            </Note>
         )}
      </FooterSummary>
   );

   return (
      <Modal
         isOpen
         onClose={isSaving ? () => {} : onClose}
         title="Ajustar localização"
         subtitle={`PED-${stop?.id ?? '—'}${
            stop?.nm ? ` · ${stop.nm}` : ''
         }`}
         maxWidth="820px"
         footer={
            <FooterBar>
               {confirmationBlock}
               <FooterActions>
                  <GhostButton
                     type="button"
                     onClick={onClose}
                     disabled={isSaving}
                  >
                     Cancelar
                  </GhostButton>
                  <PrimaryButton
                     type="button"
                     onClick={handleSave}
                     disabled={!canSave}
                     title={
                        !coord ? 'Marque o ponto no mapa primeiro.' : undefined
                     }
                  >
                     {saveLabel}
                  </PrimaryButton>
               </FooterActions>
            </FooterBar>
         }
      >
         <Body>
            {/* ───── Defina a localização — 2 colunas (referência | mapa). A
                confirmação do ponto vive no rodapé (resumo ao lado das ações). ─ */}
            <Zone>
               <ZoneTitle>Defina a localização</ZoneTitle>

               <Narrator $tone={narrator.tone}>
                  {narrator.icon}
                  <span>{narrator.text}</span>
               </Narrator>

               <DefineGrid>
                  {/* Coluna esquerda — referência (DADO, não editável). Item
                      vinculado (handoff 0044) → 6 campos atomizados; sem vínculo
                      → fallback de 2 campos mesclados. */}
                  <div className="ref">
                     <RefCaption>Endereço do pedido (referência)</RefCaption>
                     {hasAtomized ? (
                        <>
                           <AddrField>
                              <label htmlFor="ref-street">Logradouro</label>
                              <ReadBox id="ref-street">
                                 {orDash(addr.street)}
                              </ReadBox>
                           </AddrField>
                           <RefRowNumCep>
                              <AddrField>
                                 <label htmlFor="ref-number">Número</label>
                                 <input
                                    id="ref-number"
                                    value={orDash(addr.number)}
                                    readOnly
                                 />
                              </AddrField>
                              <AddrField>
                                 <label htmlFor="ref-zip">CEP</label>
                                 <input
                                    id="ref-zip"
                                    value={formatCep(addr.zip)}
                                    readOnly
                                 />
                              </AddrField>
                           </RefRowNumCep>
                           <AddrField>
                              <label htmlFor="ref-complement">Complemento</label>
                              <ReadBox id="ref-complement">
                                 {orDash(addr.complement)}
                              </ReadBox>
                           </AddrField>
                           <RefRowCityState>
                              <AddrField>
                                 <label htmlFor="ref-city">Município</label>
                                 <input
                                    id="ref-city"
                                    value={orDash(addr.city)}
                                    readOnly
                                 />
                              </AddrField>
                              <AddrField>
                                 <label htmlFor="ref-state">Estado</label>
                                 <input
                                    id="ref-state"
                                    value={orDash(addr.state)}
                                    readOnly
                                 />
                              </AddrField>
                           </RefRowCityState>
                        </>
                     ) : (
                        <>
                           <Field>
                              <label htmlFor="ref-addr">Endereço</label>
                              <textarea
                                 id="ref-addr"
                                 value={originalAddress || '—'}
                                 rows={3}
                                 readOnly
                              />
                           </Field>
                           <Field>
                              <label htmlFor="ref-compl">Complemento</label>
                              <textarea
                                 id="ref-compl"
                                 value={originalComplement || '—'}
                                 rows={2}
                                 readOnly
                              />
                           </Field>
                        </>
                     )}
                  </div>

                  {/* Coluna do meio — ferramentas (busca + mapa). */}
                  <div className="tools">
                     <RefCaption>Pesquisar e ajustar no mapa</RefCaption>
                     {useMap && (
                        <>
                           <SearchRow onSubmit={handleAddressSearch}>
                              <input
                                 ref={searchInputRef}
                                 type="text"
                                 value={searchInput}
                                 onChange={(e) => {
                                    setSearchInput(e.target.value);
                                    if (searchStatus) setSearchStatus(null);
                                 }}
                                 placeholder="Pesquisar um endereço no mapa…"
                                 aria-label="Pesquisar um endereço no mapa"
                                 disabled={isSaving}
                              />
                              <button
                                 type="submit"
                                 title="Pesquisar endereço"
                                 aria-label="Pesquisar endereço"
                                 disabled={
                                    isSaving || searchStatus === 'searching'
                                 }
                              >
                                 {HFIcon.search}
                              </button>
                           </SearchRow>
                           {searchStatus === 'searching' && (
                              <Note>Buscando endereço…</Note>
                           )}
                           {searchStatus === 'notfound' && (
                              <Note $error>
                                 Endereço não encontrado — refine o texto ou
                                 clique no mapa.
                              </Note>
                           )}
                           <MapBox>
                              <div ref={containerRef} />
                           </MapBox>
                        </>
                     )}
                     {mapLoading && <Note>Carregando mapa…</Note>}
                     {noMap && (
                        <Note>
                           Mapa indisponível — informe as coordenadas no resumo
                           do ponto (rodapé).
                        </Note>
                     )}
                  </div>
               </DefineGrid>
            </Zone>

            {error && <ErrorBox>{error}</ErrorBox>}
         </Body>
      </Modal>
   );
}

LocationPickerModal.propTypes = {
   itemId: PropTypes.number.isRequired,
   stop: PropTypes.object.isRequired,
   base: PropTypes.object,
   onClose: PropTypes.func.isRequired,
   onSaved: PropTypes.func.isRequired,
};
